import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import Webcam from 'react-webcam';
import maskCNH from '../assets/cnhCrop.svg';
import maskFace from '../assets/maskCam.png';
import AdditionalFields from '../components/AdditionalFields';
import Button from '../components/Button';
import Camera from '../components/Camera';
import { Crop } from '../components/Camera/styles';
import Choose from '../components/Choose';
import CompanyIdentification from '../components/CompanyIdentification';
import Dropzone from '../components/Dropzone';
import { FaceTecCamera } from '../components/FaceTecCamera';
import FlowChooser from '../components/FlowChooser';
import Identification from '../components/Identification';
import Instructions from '../components/Instructions/index';
import Loading from '../components/Loading';
import Result from '../components/Result';
import ServicesSelection from '../components/ServicesSelection';
import { useAppTranslation } from '../hooks/useAppTranslation';
import api from '../services/api';
import { getDocuments, IDocumentsSelect } from '../services/documents';
import { getCompanyServices, PersonType } from '../services/services';
import { MobileCam } from '../styles/global';
import { useAuth } from './authContext';

export interface Service {
  cos_ser_id: number;
  ser_name: string;
  ser_description: string;
  ser_pubsub: string;
  ser_additional_fields: boolean;
  /**
   * 1 quando o serviço consome foto (biometria facial). Opcional porque a
   * seleção de serviços monta seus objetos com um tipo local mais enxuto —
   * o valor vem preenchido da API em todos os casos reais.
   */
  ser_send_selfie?: number;
}

/**
 * Etapas possíveis do wizard. A ordem real de cada atendimento é derivada em
 * `flowSteps` a partir dos serviços contratados e das escolhas do atendente —
 * é a fonte única da numeração, consumida tanto pelo `changeStep()` quanto
 * pelo indicador de etapas (`useWizardSteps`).
 *
 * `company` é a etapa de dados do fluxo de Pessoa Jurídica (só o CNPJ) — ela
 * mesma dispara a consulta, então o fluxo PJ não passa por método, foto nem
 * instruções.
 */
export type StepKey =
  | 'services'
  | 'method'
  | 'identification'
  | 'company'
  | 'additional'
  | 'instructions'
  | 'camera'
  | 'result';
interface StepContextData {
  documentNum: string;
  name: string;
  birthday: string;
  zipCode: string;
  phoneNumber: string;
  email: string;
  selfie: string;
  currentStep: number;
  currentValidation: string;
  result: string;
  loading: boolean;
  documentType: number;
  messageError: string;
  actionFromImage: string;
  serviceError: string;
  serviceResults: IServiceResult[];
  documents: IDocumentsSelect[];
  selectedServices: Service[];
  /** Todos os serviços contratados pela empresa (para rótulos e resumo). */
  contractedServices: Service[];
  /** Falso enquanto a lista de serviços contratados não voltou da API. */
  contractedServicesLoaded: boolean;
  /** Ordem das etapas deste atendimento, derivada dos flags e das escolhas. */
  flowSteps: StepKey[];
  /**
   * Algum serviço desta consulta é de biometria facial (consome foto). Nesse
   * caso a foto é OBRIGATÓRIA — a etapa de método não oferece "preenchimento
   * de dados", porque escolher isso deixaria a biometria sem face para
   * comparar; ela abre direto na escolha do tipo de foto.
   */
  photoRequired: boolean;
  handleSetDocument: (cpfParam: string) => void;
  handleSetName: (nameParam: string) => void;
  handleSetBirthday: (birthParam: string) => void;
  handleSetZipCode: (zipCodeParam: string) => void;
  handleSetPhoneNumber: (phoneNumberParam: string) => void;
  handleSetEmail: (emailParam: string) => void;
  handleSetImage: (imageParam: string) => void;
  setSelectedServices: React.Dispatch<React.SetStateAction<Service[]>>;
  changeStep: () => React.ReactElement;
  setResult: React.Dispatch<React.SetStateAction<string>>;
  handleSetLoading: (loadingParam: boolean) => void;
  handleChangeCurrentStep: () => void;
  handleReturnCurrentStep: () => void;
  handleJumpSteps: (n: number) => void;
  handleChangeCurrentValidation: (validation: string) => void;
  skipPhotoRequested: boolean;
  setSkipPhotoRequested: (value: boolean) => void;
  flowStarted: boolean;
  setFlowStarted: (value: boolean) => void;
  /** Tipo de pessoa do atendimento em curso (Pessoa Física x Jurídica). */
  personType: PersonType;
  /** Começa (ou recomeça) um atendimento do tipo de pessoa escolhido. */
  startFlow: (type: PersonType) => void;
  clearData: () => void;
  /** Limpa os dados E volta para a escolha inicial (Pessoa Física x Jurídica). */
  resetFlow: () => void;
  setDocumentType: React.Dispatch<React.SetStateAction<number>>;
  setMessageError: (value: string) => void;
  ImageToBase64: (file: File) => void;
  setActionFromImage: (value: string) => void;
  setServiceError: (value: string) => void;
  setServiceResults: (value: IServiceResult[]) => void;
}
export interface IAdditionalDataDefendand {
  doc?: string | null;
  name?: string | null;
  type?: string | null;
}

export interface IAdditionalDataItem {
  /**
   * Serviços do protocolo novo da esteira (Score, Dados/Validação PJ) mandam
   * um objeto só, identificado por `kind` — é por ele que a tela escolhe como
   * mostrar o conteúdo (o nome do serviço pode vir nulo). Sem `kind`, é a
   * lista de processos/registros de sempre (Brick e afins).
   */
  kind?: string;
  subject?: string | null;
  branch?: string | null;
  inferredSubjectDescription?: string | null;
  type?: string | null;
  court?: string | null;
  judgingBody?: string | null;
  date?: string | null;
  status?: string | null;
  lawsuitNr?: string | null;
  value?: number | null;
  isCriminal?: boolean | null;
  defendands?: IAdditionalDataDefendand[] | null;
}

export interface IServiceResult {
  result?: string;
  message?: string;
  service?: string;
  additionalData?: IAdditionalDataItem[];
  // * Provedor não terminou de apurar tudo a tempo (ex.: timeout parcial da
  // Brick), mas já tinha capturado dado real antes disso — resultado pode
  // estar incompleto, não quer dizer que está.
  partial?: boolean;
}

export interface IValidationResponse {
  message?: string;
  result?: string;
  service?: string;
  results?: IServiceResult[];
  partial?: boolean;
}

const StepContext = createContext<StepContextData>({} as StepContextData);

export function StepProvider({ children }: { children: React.ReactNode }) {
  const history = useHistory();
  const { additionalFields, canSelectServices, requiresSelfie, companyName } =
    useAuth();

  const webcamRef = React.useRef<Webcam>();

  const [currentStep, setCurrentStep] = useState(1);
  const [name, setName] = useState('');
  const [documentNum, setDocumentNum] = useState('');
  const [selfie, setImage] = useState('');
  const [currentValidation, setCurrentValidation] = useState('');
  const [birthday, setBirthday] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState('');
  const [messageError, setMessageError] = useState('');
  const [documentType, setDocumentType] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFromImage, setActionFromImage] = useState('SELFIE');
  const [serviceError, setServiceError] = useState('');
  const [serviceResults, setServiceResults] = useState<IServiceResult[]>([]);
  const [documents, setDocuments] = useState<IDocumentsSelect[]>([]);
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [contractedServices, setContractedServices] = useState<Service[]>([]);
  const [contractedServicesLoaded, setContractedServicesLoaded] =
    useState(false);
  const [skipPhotoRequested, setSkipPhotoRequested] = useState(false);
  const [flowStarted, setFlowStarted] = useState(false);
  const [personType, setPersonType] = useState<PersonType>('PF');
  const { translationPage } = useAppTranslation();
  const { tp: tpCamera } = translationPage(tr => tr.components.camera);

  const lifeProof = localStorage.getItem('@SWINTbalcao:lifeProof');

  const handleChangeCurrentStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);

  const handleReturnCurrentStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  const handleJumpSteps = useCallback((n: number) => {
    setCurrentStep(prev => prev + n);
  }, []);

  // Quando o atendente escolheu serviços específicos, são eles que dizem se a
  // etapa de dados adicionais existe; com "todos" (lista vazia), vale o flag
  // da empresa.
  const addFields = useMemo(
    () =>
      selectedServices.length === 0
        ? additionalFields
        : selectedServices.some(s => s.ser_additional_fields === true),
    [selectedServices, additionalFields],
  );

  // Tem serviço de biometria facial no fluxo? Se tem, a foto é obrigatória e a
  // etapa de método NÃO pergunta "foto x preenchimento de dados" — só o tipo
  // de foto. Sem biometria facial a foto é opcional, e aí a pergunta faz
  // sentido (o caminho da CNH ainda serve para anexar a imagem do documento).
  //
  // A checagem sai da lista de serviços do /services (dado do contrato atual,
  // com reg_active e ser_send_selfie por serviço) e não do flag requiresSelfie
  // do login: aquele fica velho no localStorage quando o contrato da empresa
  // muda e só volta a valer no login seguinte. Se o atendente escolheu
  // serviços específicos, valem os escolhidos.
  //
  // Na dúvida (lista ainda não carregada, ou algum serviço sem o campo),
  // assume que precisa: deixar de pedir a foto de um serviço que depende dela
  // quebraria a consulta, enquanto pedir à toa só custa um passo.
  const photoRequired = useMemo(() => {
    if (!contractedServicesLoaded) return requiresSelfie;

    const servicesInFlow =
      selectedServices.length > 0 ? selectedServices : contractedServices;

    if (servicesInFlow.length === 0) return requiresSelfie;

    return servicesInFlow.some(
      service =>
        service.ser_send_selfie === undefined ||
        Number(service.ser_send_selfie) === 1,
    );
  }, [
    contractedServicesLoaded,
    contractedServices,
    selectedServices,
    requiresSelfie,
  ]);

  const flowSteps = useMemo<StepKey[]>(() => {
    const steps: StepKey[] = [];

    if (canSelectServices) steps.push('services');

    // Pessoa Jurídica: só o CNPJ. Sem foto, biometria, data de nascimento nem
    // campos adicionais — a etapa de dados já dispara a consulta.
    if (personType === 'PJ') {
      steps.push('company');
      steps.push('result');
      return steps;
    }

    // A etapa de método existe sempre, mas o que ela pergunta muda: com
    // biometria facial no fluxo, só o tipo de foto (a foto é obrigatória);
    // sem biometria, foto x preenchimento de dados. Antes ela só existia
    // quando havia biometria — e aí o atendente podia escolher "preenchimento
    // de dados" num fluxo que dependia da face, e a biometria ficava sem o
    // que comparar.
    steps.push('method');

    steps.push('identification');
    if (addFields) steps.push('additional');

    // Instructions e Camera seguem sempre na cadeia de navegação: mesmo na
    // análise só por dados é o Instructions que dispara a consulta e pula
    // direto para o resultado (handleJumpSteps). Quem decide se elas aparecem
    // no indicador é o useWizardSteps.
    steps.push('instructions');
    steps.push('camera');
    steps.push('result');

    return steps;
  }, [canSelectServices, addFields, personType]);

  const changeStep = useCallback(() => {
    // A escolha inicial (Pessoa Física x Jurídica) aparece sempre: o card de
    // um tipo não contratado fica visível, só desabilitado.
    if (!flowStarted) {
      return <FlowChooser />;
    }

    switch (flowSteps[currentStep - 1]) {
      case 'services':
        return <ServicesSelection />;
      case 'method':
        return <Choose />;
      case 'identification':
        return <Identification />;
      case 'company':
        return <CompanyIdentification />;
      case 'additional':
        return <AdditionalFields />;
      case 'instructions':
        return <Instructions />;
      case 'camera':
        return lifeProof === 'false' ||
          (lifeProof === 'true' && currentValidation !== 'Foto') ? (
          <Camera />
        ) : (
          <FaceTecCamera />
        );
      case 'result':
        return <Result />;
      default:
        return <></>;
    }
  }, [flowStarted, flowSteps, currentStep, lifeProof, currentValidation]);

  const capture = () => {
    const imageSrc = webcamRef.current?.getScreenshot() || '';
    setImage(imageSrc);
  };

  const clearData = useCallback(() => {
    setDocumentNum('');
    setImage('');
    setName('');
    setBirthday('');
    setZipCode('');
    setPhoneNumber('');
    setEmail('');
    setCurrentStep(1);
    setCurrentValidation('');
    setMessageError('');
    setResult('');
    setServiceError('');
    setSkipPhotoRequested(false);
    setServiceResults([]);
  }, []);

  // O reset de dados roda sempre que o passo volta para 1; a escolha do fluxo
  // (Pessoa Física x Jurídica) só é desfeita explicitamente, por aqui.
  const resetFlow = useCallback(() => {
    clearData();
    setFlowStarted(false);
  }, [clearData]);

  // Trocar de tipo de pessoa descarta a seleção de serviços: os serviços de
  // um tipo não existem no outro. No mesmo tipo a seleção é mantida, como já
  // era entre um atendimento e o seguinte.
  const startFlow = useCallback(
    (type: PersonType) => {
      clearData();
      if (type !== personType) setSelectedServices([]);
      setPersonType(type);
      setFlowStarted(true);
    },
    [clearData, personType],
  );

  const ImageToBase64 = useCallback((file: File): void => {
    const reader: FileReader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (): void => {
      setImage(reader.result as string);
    };
    reader.onerror = () => {
      return false;
    };
  }, []);

  useEffect(() => {
    const onLocationFailed = () => {
      setLoading(false);
      history.push('/geolocationDenied');
    };

    const getUserLocation = () => {
      setLoading(true);
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(position => {
          const { latitude, longitude } = position.coords;

          localStorage.setItem('@SWINTbalcao:latitude', String(latitude));
          localStorage.setItem('@SWINTbalcao:longitude', String(longitude));
          api.defaults.headers.common.lat = latitude;
          api.defaults.headers.common.lng = longitude;
          api.defaults.headers.common.cmpid = '';
          api.defaults.headers.common.plcid = '';
          setLoading(false);
        }, onLocationFailed);
      } else {
        onLocationFailed();
      }
    };

    const getOptions = () => {
      getDocuments().then(response => {
        if (response) setDocuments(response);
      });
    };
    //getUserLocation();
    getOptions();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (currentStep === 1) {
      clearData();
    }
  }, [currentStep, clearData]);

  // Lista de serviços contratados do tipo de pessoa do atendimento — usada
  // para rotular os apontamentos por serviço e para listar tudo no resumo.
  useEffect(() => {
    if (!companyName) return undefined;

    // A lista do tipo anterior não vale para o novo: até a nova voltar, o
    // fluxo trata como "ainda não carregada".
    setContractedServices([]);
    setContractedServicesLoaded(false);

    const controller = new AbortController();
    getCompanyServices(controller.signal, personType).then(services => {
      if (controller.signal.aborted) return;
      if (Array.isArray(services)) setContractedServices(services);
      setContractedServicesLoaded(true);
    });

    return () => controller.abort();
  }, [companyName, personType]);

  const value = useMemo(
    () => ({
      loading,
      documentNum,
      name,
      birthday,
      zipCode,
      phoneNumber,
      email,
      selfie,
      currentStep,
      result,
      documentType,
      selectedServices,
      contractedServices,
      contractedServicesLoaded,
      flowSteps,
      photoRequired,
      handleSetDocument: setDocumentNum,
      handleSetName: setName,
      handleSetBirthday: setBirthday,
      handleSetZipCode: setZipCode,
      handleSetPhoneNumber: setPhoneNumber,
      handleSetEmail: setEmail,
      handleSetImage: setImage,
      handleSetLoading: setLoading,
      handleChangeCurrentValidation: setCurrentValidation,
      skipPhotoRequested,
      setSkipPhotoRequested,
      flowStarted,
      setFlowStarted,
      personType,
      startFlow,
      setResult,
      setDocumentType,
      setMessageError,
      setActionFromImage,
      setServiceError,
      serviceResults,
      setServiceResults,
      setSelectedServices,
      handleChangeCurrentStep,
      handleReturnCurrentStep,
      handleJumpSteps,
      changeStep,
      clearData,
      resetFlow,
      ImageToBase64,
      currentValidation,
      messageError,
      actionFromImage,
      serviceError,
      documents,
    }),
    [
      loading,
      documentNum,
      name,
      birthday,
      zipCode,
      phoneNumber,
      email,
      selfie,
      currentStep,
      result,
      documentType,
      selectedServices,
      contractedServices,
      contractedServicesLoaded,
      flowSteps,
      photoRequired,
      handleChangeCurrentStep,
      handleReturnCurrentStep,
      handleJumpSteps,
      changeStep,
      clearData,
      resetFlow,
      ImageToBase64,
      currentValidation,
      messageError,
      actionFromImage,
      serviceError,
      serviceResults,
      documents,
      skipPhotoRequested,
      flowStarted,
      personType,
      startFlow,
    ],
  );

  return (
    <StepContext.Provider value={value}>
      {/* Antes o gate era hasPhotoFlow ("a empresa tem serviço de biometria"),
          que agora não decide mais nada sozinho: sem biometria o atendente
          ainda pode escolher analisar por foto. Quem manda é a escolha dele —
          e ela é o que já leva o fluxo até a etapa de câmera. */}
      {isMobile &&
      (currentValidation === 'CNH' || currentValidation === 'Foto') &&
      flowSteps[currentStep - 1] === 'camera' &&
      !selfie &&
      (lifeProof === 'false' ||
        (lifeProof === 'true' && currentValidation !== 'Foto')) ? (
        <MobileCam>
          <section>
            {actionFromImage === 'SELFIE' && (
              <>
                <Webcam
                  audio={false}
                  ref={webcamRef as any}
                  screenshotQuality={1}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{
                    facingMode:
                      currentValidation === 'Foto' ? 'user' : 'environment',
                  }}
                  style={{
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                    background: 'rgba(0, 0, 0, 0.85)',
                  }}
                />
                <Crop>
                  {currentValidation === 'Foto' ? (
                    <img src={maskFace} alt="" height="80%" />
                  ) : (
                    <img src={maskCNH} alt="" height="98%" />
                  )}
                </Crop>
              </>
            )}
            {actionFromImage === 'UPLOAD' && <Dropzone />}
          </section>
          {currentValidation === 'CNH' && actionFromImage === 'SELFIE' && (
            <Button
              title={tpCamera('uploadImage')}
              onClick={() => setActionFromImage('UPLOAD')}
            />
          )}
          {actionFromImage === 'UPLOAD' && (
            <Button
              title={tpCamera('camera')}
              onClick={() => setActionFromImage('SELFIE')}
            />
          )}
          {actionFromImage === 'SELFIE' && (
            <Button
              title={tpCamera('takePhoto')}
              onClick={capture}
              style={{ alignSelf: 'center' }}
            />
          )}
        </MobileCam>
      ) : (
        <>{children}</>
      )}
      {loading && <Loading />}
    </StepContext.Provider>
  );
}

export function useSteps(): StepContextData {
  const context = useContext(StepContext);

  if (!context) {
    throw new Error(
      "Custom hook 'useSteps' must be used within an 'StepProvider'",
    );
  }

  return context;
}
